import { Elements } from "@stripe/react-stripe-js";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { Grid, IconButton, Tooltip } from "@mui/material";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import PaymentDrawer from "@/components/app/provider/billing/payment/PaymentDrawer";
import { getStripePromise } from "@/queries/stripe/use-stripe";
import CareTeamCard from "./components/CareTeamCard";
import CheckInPatientTable from "./components/CheckInPatientTable";
import CheckInSummaryCard from "./components/CheckInSummaryCard";
import GroupPageShell from "./components/GroupPageShell";
import {
  CHECK_IN_LABEL,
  CHECK_IN_PAYMENT_CATEGORY,
} from "./group-visit-constant";
import { canStartSession } from "./group-visit-utils";
import { useCheckInActions } from "./hooks/use-check-in-actions";
import { useGroupCheckIn } from "./hooks/use-group-check-in";
import { CheckInPatient } from "./group-visit-types";

/**
 * Check-in screen for a group session — the roster with each patient's
 * payment state, ahead of starting the session.
 *
 * @author Rajkumar Rathod
 */
const GroupCheckInLayout = () => {
  const { session, group, patients, isLoading, isRefreshing, title } =
    useGroupCheckIn();
  const { onRowAction, startSession, paymentDisclosure, refetchSession } =
    useCheckInActions(session.uuid, session.status);
  const payingPatient = paymentDisclosure.data as CheckInPatient | undefined;

  return (
    <GroupPageShell
      title={title}
      actions={
        <StyledButtonV2
          label={CHECK_IN_LABEL.startSession}
          variant="primary"
          disabled={!patients.length || !canStartSession(session.status)}
          onClick={startSession}
        />
      }
    >
      <Flex flexDirection="column" gap={2}>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "stretch",
          }}
        >
          <Grid
            size={{
              xs: 12,
              lg: 7,
            }}
          >
            <CheckInSummaryCard session={session} />
          </Grid>

          <Grid
            size={{
              xs: 12,
              lg: 5,
            }}
          >
            <CareTeamCard group={group} />
          </Grid>
        </Grid>

        <Flex justifyContent="flex-end" mb={-1.5}>
          <Tooltip title={CHECK_IN_LABEL.refresh}>
            <span>
              <IconButton
                size="small"
                aria-label={CHECK_IN_LABEL.refresh}
                disabled={isRefreshing}
                onClick={refetchSession}
              >
                <RefreshOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Flex>

        {!isLoading && !patients.length ? (
          <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
            {CHECK_IN_LABEL.emptyRoster}
          </Text>
        ) : (
          <CheckInPatientTable
            patients={patients}
            isLoading={isLoading}
            onRowAction={onRowAction}
          />
        )}
      </Flex>

      <Elements stripe={getStripePromise()}>
        <PaymentDrawer
          disclosureHost={paymentDisclosure}
          paymentCategory={CHECK_IN_PAYMENT_CATEGORY}
          amount={payingPatient?.amount ? `${payingPatient.amount}` : undefined}
          providerId={
            session.therapistId
              ? {
                  uuid: session.therapistId,
                  name: session.therapistName ?? "",
                }
              : undefined
          }
          onSuccess={refetchSession}
        />
      </Elements>
    </GroupPageShell>
  );
};

export default GroupCheckInLayout;
